import React, { useState, useEffect, useContext } from "react";
import { Table, Button, Badge, Modal, Form, Card, Row, Col, Alert, Pagination, Spinner } from "react-bootstrap";
import axiosClient from "../../config/axiosClient";
import API_ENDPOINTS from "../../config/api"; 
import UserContext from "../../context/UserContext";

const OTManagement = () => {
  const { user } = useContext(UserContext);
  const [requests, setRequests] = useState([]);
  const [employees, setEmployees] = useState([]); 
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false); // Trạng thái chờ xử lý

  // --- PAGINATION STATE ---
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); 

  const [newRequest, setNewRequest] = useState({
    title: "",
    content: "",
    startTime: "",
    endTime: "",
    totalHours: 0,
    targetUserId: ""
  });

  const [serverError, setServerError] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchMyRequests();
    fetchEmployees();
    const interval = setInterval(fetchMyRequests, 15000);
    return () => clearInterval(interval);
  }, [user]);

  const getUserId = () => {
    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    return user?.id || user?.userId || storedUser?.id || storedUser?.userId;
  };

  const fetchEmployees = async () => {
    try {
      const res = await axiosClient.get(API_ENDPOINTS.users);
      setEmployees(res.data || []);
    } catch (err) {
      console.error("Load employees error:", err);
    }
  };

  const fetchMyRequests = async () => {
    try {
      const uId = getUserId();
      if (!uId) return;
      const res = await axiosClient.get(API_ENDPOINTS.otRequests);
      const myData = res.data.filter(r => 
        String(r.userId) === String(uId) || String(r.createdById) === String(uId)
      );
      setRequests(myData);
    } catch (err) {
      console.error("Load OT requests error:", err);
    }
  };

  // --- PAGINATION LOGIC ---
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = requests.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(requests.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const calculateHours = (start, end) => {
    if (!start || !end) return 0;
    const startDate = new Date(start);
    const endDate = new Date(end);
    if (endDate <= startDate) return 0;
    const diffMs = endDate - startDate;
    const hours = diffMs / (1000 * 60 * 60);
    return parseFloat(hours.toFixed(2));
  };

  const validate = () => {
    let newErrors = {};
    if (!newRequest.targetUserId) newErrors.targetUserId = "Please select an employee!";
    if (!newRequest.title) newErrors.title = "Title is required!";
    if (!newRequest.content?.trim()) newErrors.content = "Reason is required!";
    if (!newRequest.startTime) newErrors.startTime = "Start time is required!";
    if (!newRequest.endTime) newErrors.endTime = "End time is required!";
    if (newRequest.startTime && newRequest.endTime) {
      if (new Date(newRequest.endTime) <= new Date(newRequest.startTime)) {
        newErrors.endTime = "End time must be after start time!";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleDateTimeChange = (field, value) => {
    const updatedRequest = { ...newRequest, [field]: value };
    const hours = calculateHours(updatedRequest.startTime, updatedRequest.endTime);
    setNewRequest({ ...updatedRequest, totalHours: hours });
    if (errors[field]) setErrors({ ...errors, [field]: null });
    setServerError(null);
  };

  const handleOpenCreate = () => {
    setIsEditing(false);
    setIsViewing(false);
    setIsSubmitting(false);
    const uId = getUserId();
    setNewRequest({ 
      title: "", 
      content: "", 
      startTime: "", 
      endTime: "", 
      totalHours: 0, 
      targetUserId: uId ? String(uId) : ""
    });
    setErrors({});
    setServerError(null);
    setShowModal(true);
  };

  const handleOpenEdit = (req) => {
    setIsEditing(true);
    setIsViewing(false);
    setIsSubmitting(false);
    setCurrentId(req.id);
    setNewRequest({
      title: req.title,
      content: req.content,
      startTime: req.startTime ? req.startTime.substring(0, 16) : "", 
      endTime: req.endTime ? req.endTime.substring(0, 16) : "",
      totalHours: req.totalHours,
      targetUserId: String(req.userId)
    });
    setErrors({});
    setServerError(null);
    setShowModal(true);
  };

  const handleView = (req) => {
    setIsViewing(true);
    setIsEditing(false);
    setNewRequest({
      title: req.title,
      content: req.content,
      startTime: req.startTime ? req.startTime.substring(0, 16) : "",
      endTime: req.endTime ? req.endTime.substring(0, 16) : "",
      totalHours: req.totalHours,
      targetUserId: String(req.userId)
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this OT request?")) {
      try {
        await axiosClient.delete(`${API_ENDPOINTS.otRequests}/${id}`);
        fetchMyRequests();
      } catch (err) {
        alert("Failed to delete request. Please try again.");
      }
    }
  };

  const handleSubmit = async () => {
    if (!validate() || isSubmitting) return;
    
    setIsSubmitting(true);
    setServerError(null);
    const uId = getUserId();
    
    const payload = {
      id: isEditing ? currentId : 0,
      title: newRequest.title,
      content: newRequest.content,
      startTime: newRequest.startTime,
      endTime: newRequest.endTime,
      totalHours: parseFloat(newRequest.totalHours),
      userId: parseInt(newRequest.targetUserId), 
      createdById: parseInt(uId),
      status: 1
    };

    try {
      if (isEditing) {
        await axiosClient.put(`${API_ENDPOINTS.otRequests}/${currentId}`, payload);
      } else {
        await axiosClient.post(API_ENDPOINTS.otRequests, payload);
      }
      setShowModal(false);
      fetchMyRequests();
    } catch (err) {
      const errorData = err.response?.data;
      setServerError(typeof errorData === 'string' ? errorData : errorData?.title || "Internal Server Error (500)");
      setIsSubmitting(false); // Cho phép bấm lại nếu lỗi
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      0: { bg: "secondary", text: "DRAFT" },
      1: { bg: "warning", text: "PENDING", color: "dark" },
      2: { bg: "success", text: "APPROVED" },
      3: { bg: "danger", text: "REJECTED" },
    };
    const config = badges[status] || { bg: "info", text: "UNKNOWN" };
    return <Badge bg={config.bg} text={config.color || "white"} className="fw-normal rounded-pill px-3 py-2">{config.text}</Badge>;
  };

  const getFullName = (userData) => {
    if (!userData) return null;
    return userData.employeeLeaveDetail?.fullName || userData.fullName || null;
  };

  const getCurrentApproverName = (request) => {
    if (!request.approvalLogs || request.approvalLogs.length === 0) return null;
    const currentLog = request.approvalLogs.find(log => log.stepOrder === request.currentStep);
    return getFullName(currentLog?.approver);
  };

  return (
    <div className="container-fluid py-4" style={{ backgroundColor: "#f4f7f9", minHeight: "100vh" }}>
      <div className="d-flex justify-content-between align-items-center mb-4 px-2">
        <div>
          <h3 className="fw-bold text-dark mb-1" style={{ letterSpacing: "-0.5px" }}>OT MANAGEMENT</h3>
          <p className="text-muted small mb-0">Track and manage overtime requests</p>
        </div>
        <Button variant="primary" className="px-4 py-2 shadow-sm fw-bold rounded-pill border-0" style={{ backgroundColor: '#0d6efd' }} onClick={handleOpenCreate}>
          <i className="fas fa-plus me-2"></i>REQUEST OT
        </Button>
      </div>

      <Card className="shadow-sm border-0 rounded-4 overflow-hidden">
        <Card.Body className="p-0">
          <Table hover responsive className="mb-0 align-middle">
            <thead style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #edf2f7" }}>
              <tr>
                <th className="ps-4 py-4 text-muted fw-bold small text-uppercase" style={{ width: '20%' }}>Title</th>
                <th className="py-4 text-muted fw-bold small text-uppercase" style={{ width: '25%' }}>Employee OT</th>
                <th className="py-4 text-muted fw-bold small text-uppercase" style={{ width: '20%' }}>Time Range</th>
                <th className="py-4 text-muted fw-bold small text-center text-uppercase" style={{ width: '10%' }}>Hours</th>
                <th className="py-4 text-muted fw-bold small text-uppercase" style={{ width: '15%' }}>Current Approver</th>
                <th className="py-4 text-muted fw-bold small text-uppercase" style={{ width: '10%' }}>Status</th>
                <th className="py-4 text-end pe-4 text-muted fw-bold small text-uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((r) => (
                <tr key={r.id} style={{ transition: "all 0.2s" }}>
                  <td className="ps-4 py-4">
                    <div className="text-primary fw-bold" style={{ fontSize: '1rem', lineHeight: '1.4' }}>{r.title}</div>
                  </td>
                  <td className="py-4">
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle bg-light d-flex align-items-center justify-content-center me-3" style={{ width: '38px', height: '38px' }}>
                        <i className="fas fa-user text-secondary"></i>
                      </div>
                      <div style={{ lineHeight: '1.5' }}>
                        <div className="fw-bold text-dark text-uppercase small">{getFullName(r.user) || "---"}</div>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                          Created by: <span className="fw-medium">{getFullName(r.createdBy) || "---"}</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="py-4">
                    <div className="small text-secondary" style={{ lineHeight: '1.6' }}>
                      <div className="d-flex align-items-center mb-1">
                        <i className="far fa-calendar-alt me-2 opacity-50"></i>
                        {new Date(r.startTime).toLocaleString("en-GB", { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </div>
                      <div className="d-flex align-items-center">
                        <i className="fas fa-long-arrow-alt-right me-2 opacity-50"></i>
                        {new Date(r.endTime).toLocaleString("en-GB", { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </td>
                  <td className="text-center py-4">
                    <span className="badge rounded-pill bg-soft-primary text-primary border px-3 py-2" style={{ backgroundColor: '#e7f1ff', fontSize: '0.85rem' }}>
                        {r.totalHours}h
                    </span>
                  </td>
                  <td className="py-4">
                    <div style={{ lineHeight: '1.5' }}>
                      <div className="small fw-bold text-dark">
                        <i className="fas fa-user-shield me-2 text-info opacity-75"></i>
                        {getCurrentApproverName(r) || "---"}
                      </div>
                      <div className="text-muted ps-4" style={{ fontSize: '0.75rem' }}>Step {r.currentStep}</div>
                    </div>
                  </td>
                  <td className="py-4">{getStatusBadge(r.status)}</td>
                  <td className="text-end pe-4 py-4">
                    <div className="d-flex justify-content-end gap-2">
                      <Button variant="light" className="btn-icon rounded-circle border shadow-sm text-info" style={{ width: '36px', height: '36px', padding: '0' }} onClick={() => handleView(r)}>
                        <i className="fas fa-eye"></i>
                      </Button>
                      {r.status === 1 && ( 
                        <>
                          <Button variant="light" className="btn-icon rounded-circle border shadow-sm text-primary" style={{ width: '36px', height: '36px', padding: '0' }} onClick={() => handleOpenEdit(r)}>
                            <i className="fas fa-pencil-alt"></i>
                          </Button>
                          <Button variant="light" className="btn-icon rounded-circle border shadow-sm text-danger" style={{ width: '36px', height: '36px', padding: '0' }} onClick={() => handleDelete(r.id)}>
                            <i className="fas fa-trash-alt"></i>
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {requests.length > 0 && (
            <div className="d-flex justify-content-between align-items-center px-4 py-3 bg-light border-top">
              <span className="text-muted small">
                Showing <strong>{indexOfFirstItem + 1}</strong> - <strong>{Math.min(indexOfLastItem, requests.length)}</strong> of <strong>{requests.length}</strong>
              </span>
              {totalPages > 1 && (
                <Pagination className="mb-0 shadow-sm">
                  <Pagination.Prev disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)} />
                  {[...Array(totalPages)].map((_, i) => (
                    <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => handlePageChange(i + 1)}>
                      {i + 1}
                    </Pagination.Item>
                  ))}
                  <Pagination.Next disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)} />
                </Pagination>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => !isSubmitting && setShowModal(false)} centered size="lg">
        <Modal.Header closeButton={!isSubmitting} className="bg-primary text-white border-0 py-3">
          <Modal.Title className="fw-bold fs-5 text-uppercase ps-2">
            {isViewing ? "View OT Request" : isEditing ? "Update OT Request" : "New OT Request"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {serverError && <Alert variant="danger" className="small fw-bold shadow-sm">{serverError}</Alert>}
          <Form>
            <Form.Group className="mb-4">
              <Form.Label className="small fw-bold text-muted mb-2 text-uppercase">Request Title *</Form.Label>
              <Form.Control 
                type="text" 
                className="py-2 border-2"
                placeholder="Ex: Technical support for Australian branch"
                value={newRequest.title} 
                isInvalid={!!errors.title}
                onChange={(e) => setNewRequest({...newRequest, title: e.target.value})}
                readOnly={isViewing || isSubmitting}
              />
              <Form.Control.Feedback type="invalid">{errors.title}</Form.Control.Feedback>
            </Form.Group>

            <Row className="mb-4">
              <Col md={12}>
                <Form.Label className="small fw-bold text-muted mb-2 text-uppercase">Employee Performing OT *</Form.Label>
                <Form.Select 
                  className="py-2 border-2"
                  value={newRequest.targetUserId}
                  isInvalid={!!errors.targetUserId}
                  disabled={isViewing || isEditing || isSubmitting}
                  onChange={(e) => setNewRequest({...newRequest, targetUserId: e.target.value})}
                >
                  <option value="">-- Select Employee --</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>{getFullName(emp) || emp.userName} (ID: {emp.id})</option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">{errors.targetUserId}</Form.Control.Feedback>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col md={6}>
                <Form.Label className="small fw-bold text-muted mb-2 text-uppercase">Start Time *</Form.Label>
                <Form.Control type="datetime-local" className="py-2 border-2" isInvalid={!!errors.startTime} value={newRequest.startTime} onChange={(e) => handleDateTimeChange("startTime", e.target.value)} readOnly={isViewing || isSubmitting} />
                <Form.Control.Feedback type="invalid">{errors.startTime}</Form.Control.Feedback>
              </Col>
              <Col md={6}>
                <Form.Label className="small fw-bold text-muted mb-2 text-uppercase">End Time *</Form.Label>
                <Form.Control type="datetime-local" className="py-2 border-2" isInvalid={!!errors.endTime} value={newRequest.endTime} onChange={(e) => handleDateTimeChange("endTime", e.target.value)} readOnly={isViewing || isSubmitting} />
                <Form.Control.Feedback type="invalid">{errors.endTime}</Form.Control.Feedback>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col md={12}>
                <Form.Label className="small fw-bold text-muted mb-2 text-uppercase">Total Calculated Hours</Form.Label>
                <Form.Control type="text" value={`${newRequest.totalHours} hours`} readOnly className="bg-light fw-bold text-primary py-2 border-2" />
              </Col>
            </Row>

            <Form.Group className="mb-2">
              <Form.Label className="small fw-bold text-muted mb-2 text-uppercase">Detailed Reason *</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={4} 
                className="border-2"
                placeholder="Explain why this overtime is necessary..." 
                value={newRequest.content} 
                isInvalid={!!errors.content} 
                onChange={(e) => setNewRequest({...newRequest, content: e.target.value})} 
                readOnly={isViewing || isSubmitting} 
              />
              <Form.Control.Feedback type="invalid">{errors.content}</Form.Control.Feedback>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 p-4 pt-0">
          <Button variant="link" disabled={isSubmitting} className="text-secondary fw-bold text-decoration-none me-auto" onClick={() => setShowModal(false)}>CANCEL</Button>
          {!isViewing && (
            <Button variant="primary" disabled={isSubmitting} className="px-5 py-2 fw-bold shadow-sm rounded-pill border-0" onClick={handleSubmit}>
              {isSubmitting ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                  PROCESSING...
                </>
              ) : (
                isEditing ? "SAVE CHANGES" : "SUBMIT REQUEST"
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OTManagement;